import type { CourtRank, Locale, Suit } from './types.js';

/**
 * Search vocabulary. Every term here is matched regardless of the UI language,
 * because the SPEC requires the filter to be bilingual even when the interface
 * is not: a Spanish speaker using the English UI can still type "tres".
 */

export const SUIT_TERMS: Record<Suit, Record<Locale, string[]>> = {
  major: {
    en: ['major', 'major arcana', 'arcana', 'trump', 'trumps'],
    es: ['mayor', 'arcanos mayores', 'arcano', 'triunfo', 'triunfos'],
  },
  wands: {
    en: ['wands', 'wand', 'rods', 'rod', 'staves', 'staff', 'batons', 'clubs', 'fire'],
    es: ['bastos', 'basto', 'varas', 'vara', 'bastones', 'fuego'],
  },
  cups: {
    en: ['cups', 'cup', 'chalices', 'chalice', 'hearts', 'water'],
    es: ['copas', 'copa', 'calices', 'caliz', 'corazones', 'agua'],
  },
  swords: {
    en: ['swords', 'sword', 'blades', 'blade', 'spades', 'air'],
    es: ['espadas', 'espada', 'picas', 'aire'],
  },
  pentacles: {
    en: ['pentacles', 'pentacle', 'coins', 'coin', 'discs', 'disc', 'diamonds', 'earth'],
    es: ['oros', 'oro', 'monedas', 'moneda', 'discos', 'disco', 'pentaculos', 'tierra'],
  },
};

/** Display label for a suit. "pentacles" is shown as Coins / Oros. */
export const SUIT_LABEL: Record<Suit, Record<Locale, string>> = {
  major: { en: 'Major Arcana', es: 'Arcanos Mayores' },
  wands: { en: 'Wands', es: 'Bastos' },
  cups: { en: 'Cups', es: 'Copas' },
  swords: { en: 'Swords', es: 'Espadas' },
  pentacles: { en: 'Coins', es: 'Oros' },
};

export const RANK_TERMS: Record<CourtRank, Record<Locale, string[]>> = {
  page: { en: ['page', 'jack', 'knave'], es: ['sota', 'paje'] },
  knight: { en: ['knight'], es: ['caballo', 'caballero'] },
  queen: { en: ['queen'], es: ['reina'] },
  king: { en: ['king'], es: ['rey'] },
};

export const RANK_LABEL: Record<CourtRank, Record<Locale, string>> = {
  page: { en: 'Page', es: 'Sota' },
  knight: { en: 'Knight', es: 'Caballo' },
  queen: { en: 'Queen', es: 'Reina' },
  king: { en: 'King', es: 'Rey' },
};

export const NUMBER_WORDS: Record<Locale, string[]> = {
  en: [
    'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight',
    'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
    'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'twentyone',
  ],
  es: [
    'cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho',
    'nueve', 'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciseis',
    'diecisiete', 'dieciocho', 'diecinueve', 'veinte', 'veintiuno',
  ],
};

export const ROMAN: string[] = [
  '0', 'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
  'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx', 'xxi',
];

/** The Ace answers to more names than its number. */
export const ACE_TERMS: Record<Locale, string[]> = {
  en: ['ace', 'aces'],
  es: ['as', 'ases'],
};

/**
 * Dropped before matching so "three of cups" and "tres de copas" behave like
 * "three cups". Without this, "of" would fail to match any token and the
 * all-terms-must-match rule would return nothing.
 */
export const STOPWORDS = new Set([
  'of', 'the', 'a', 'an', 'and',
  'de', 'del', 'el', 'la', 'los', 'las', 'un', 'una', 'y',
]);

export function courtRankFor(number: number): CourtRank | null {
  switch (number) {
    case 11: return 'page';
    case 12: return 'knight';
    case 13: return 'queen';
    case 14: return 'king';
    default: return null;
  }
}
